import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { ArrowRight, MapPin, Phone, Star, Clock, Laptop2 } from "lucide-react";
import SaleTag from "./SaleTag";

const ModernHero = () => {
  return (
    <section className="relative py-16 md:py-24 lg:py-32 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/50 via-background to-background" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-12 lg:gap-20 lg:grid-cols-2">
          
          {/* Left Content */}
          <div className="space-y-8 lg:space-y-10">
            
            {/* Service Badge */}
            <div className="inline-flex items-center gap-3 rounded-full bg-primary/10 px-5 py-2.5 text-sm font-semibold text-primary border border-primary/20">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
              </span>
              IT-Support • Beratung • Schulung
            </div>
            
            {/* Main Headline - Much Larger */}
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-foreground leading-[1.1]">
                Technik-
                <br />
                Probleme?
                <span className="block text-primary mt-2">
                  Wir helfen.
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-xl">
                Geduldige, verständliche IT-Hilfe für <strong className="text-foreground">Senioren, Privatleute und Kleinunternehmen</strong> in Waren (Müritz).
              </p>
            </div>
            
            {/* CTA Buttons - Larger */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-7 text-lg font-bold rounded-xl shadow-xl transition-all hover:shadow-2xl hover:-translate-y-0.5"
              >
                Kostenloses Erstgespräch
                <ArrowRight className="ml-3 h-5 w-5" />
              </Button>
              
              <Button
                onClick={() => document.querySelector("#preise")?.scrollIntoView({ behavior: "smooth" })}
                variant="outline"
                className="border-2 border-border hover:border-primary text-foreground hover:text-primary px-8 py-7 text-lg font-bold rounded-xl transition-all"
              >
                Betreuungspakete
              </Button>
            </div>
            
            {/* Phone CTA */}
            <a
              href="tel:+4915128958006"
              className="inline-flex items-center gap-3 text-muted-foreground hover:text-primary text-lg font-medium transition-all group"
            >
              <div className="p-2 rounded-full bg-muted group-hover:bg-primary/10 transition-colors">
                <Phone className="h-5 w-5" />
              </div>
              Oder rufen Sie an: 01512 895 800 6
            </a>
            
            {/* Trust Indicator */}
            <div className="flex items-center gap-4 bg-muted/50 rounded-2xl px-6 py-4 border border-border">
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                ))}
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <div className="font-semibold text-foreground">Zufriedene Kunden</div>
                <div className="text-sm text-muted-foreground">in Waren (Müritz) & Umgebung</div>
              </div>
            </div>
          </div>
          
          {/* Right Content - Hero Image */}
          <div className="relative">
            {/* Verkaufsschild für Klartext-Check */}
            <SaleTag />
            
            {/* Background decoration */}
            <div className="absolute -top-4 -right-4 w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl" />
            
            <Card className="relative overflow-hidden rounded-3xl border-0 shadow-2xl">
              <CardContent className="p-0 relative">
                <img 
                  src="https://images.unsplash.com/photo-1618171889969-0feeb769fe78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxJVCUyMHN1cHBvcnQlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc1NjIzNjgzN3ww&ixlib=rb-4.1.0&q=85" 
                  alt="IT-Support: Hilfe bei Computer, Smartphone und Tablet" 
                  className="h-[400px] lg:h-[500px] w-full object-cover" 
                />
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                
                {/* Info Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-background/95 backdrop-blur-md rounded-2xl p-5 shadow-xl border border-border">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                          <Laptop2 className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <div>
                          <div className="font-bold text-foreground text-lg">Klartech Müritz</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            Waren (Müritz)
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-lg font-semibold text-sm">
                        <Clock className="h-4 w-4" />
                        Mo-Fr verfügbar
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModernHero;
